import { useCallback, useEffect, useRef } from 'react';

/**
 * The dialog contract shared by every wedding modal.
 *
 * BingoInterface.jsx already does this by hand for the bingo dialogs; this
 * file is the same behaviour factored out so the wedding modals answer to one
 * mechanism rather than each inventing their own. Four promises, and a
 * half-kept promise is worse than none:
 *
 *  1. `aria-modal="true"` says "nothing behind me exists". Without matching
 *     `inert` on the background, a screen-reader or keyboard user tabs
 *     straight out of the dialog into a page the dialog claims is not there.
 *  2. Focus starts inside the dialog — on its heading, not its first field,
 *     so opening a sheet on a phone does not immediately throw up a native
 *     date picker or the keyboard.
 *  3. Tab and Shift+Tab wrap inside the dialog.
 *  4. Closing hands focus back to the control that opened it. Otherwise a
 *     keyboard user who edits one task is dumped at the top of the document
 *     and has to tab back down through the whole board.
 *
 * Escape and a backdrop click both close, so the dialog is never a trap.
 */

// Everything a keyboard can land on inside a dialog. Matches the selector in
// BingoInterface.jsx.
export const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Wire up the contract above.
 *
 * @param {boolean}  open           Whether the dialog is currently rendered.
 * @param {Function} onClose        Called for Escape. May change identity every
 *                                  render — it is read through a ref so it
 *                                  never re-triggers the focus effect.
 * @param {object}   dialogRef      Ref to the dialog panel (the element that
 *                                  carries role="dialog"). Focus moves to the
 *                                  `[data-dialog-heading]` inside it.
 * @param {object}   [backgroundRef] Ref to the page content behind the dialog.
 *                                  Marked `inert` + `aria-hidden` while open.
 *                                  The dialog itself must NOT be inside it —
 *                                  the wedding budget modals portal to
 *                                  document.body for exactly this reason.
 * @returns {Function} keydown handler to put on the dialog panel.
 */
export function useDialogA11y({ open, onClose, dialogRef, backgroundRef }) {
    // Read through a ref so an inline arrow function for onClose does not make
    // the focus effect re-run on every render — which would yank focus back to
    // the heading mid-typing and re-capture the "launcher" as the field the
    // user was in.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // DECLARED FIRST, deliberately. Effect cleanups run in declaration order,
    // so `inert` is gone from the background before the focus-restore cleanup
    // below tries to focus a control inside it. An inert element cannot take
    // focus, so the other order silently drops the user at <body>.
    useEffect(() => {
        const background = backgroundRef?.current;
        if (!background || !open) return undefined;
        // Set through the DOM: React 18 does not pass `inert` through JSX, and
        // `aria-hidden` alone still leaves every control behind the dialog
        // clickable and tabbable.
        background.setAttribute('inert', '');
        background.setAttribute('aria-hidden', 'true');
        return () => {
            background.removeAttribute('inert');
            background.removeAttribute('aria-hidden');
        };
    }, [open, backgroundRef]);

    useEffect(() => {
        if (!open) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onCloseRef.current?.();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    // Move focus in on open, give it back on close. `document.activeElement`
    // read here is still the control the user activated: React has re-rendered
    // but has not moved focus.
    useEffect(() => {
        if (!open) return undefined;
        const launcher = document.activeElement;
        const dialog = dialogRef.current;
        const heading = dialog?.querySelector('[data-dialog-heading]');
        (heading || dialog)?.focus();
        return () => {
            if (
                launcher
                && typeof launcher.focus === 'function'
                && document.body.contains(launcher)
            ) {
                launcher.focus();
            }
        };
    }, [open, dialogRef]);

    // Keep Tab and Shift+Tab inside the dialog. The heading is tabindex="-1"
    // and so is not in the cycle: index -1 means "just entered", and wraps to
    // whichever end the user is heading for.
    return useCallback((e) => {
        if (e.key !== 'Tab') return;
        const dialog = dialogRef.current;
        if (!dialog) return;

        const focusables = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
            .filter((el) => el.getClientRects().length > 0);
        if (focusables.length === 0) {
            e.preventDefault();
            return;
        }

        const index = focusables.indexOf(document.activeElement);
        if (e.shiftKey) {
            if (index <= 0) {
                e.preventDefault();
                focusables[focusables.length - 1].focus();
            }
        } else if (index === -1 || index === focusables.length - 1) {
            e.preventDefault();
            focusables[0].focus();
        }
    }, [dialogRef]);
}

export default useDialogA11y;
